import { CustomerInsightDetail } from "@/types/insights";

type SeverityColor = "error" | "warning" | "default";
export const getInsightSeverityColor = (severity: string): SeverityColor => {
    switch (severity) {
        case "critical":
            return "error";
        case "warning":
            return "warning";
        default:
            return "default";
    }
};

export const getSeverityPriority = (severity: string): number => {
    if (severity === "critical") return 3;
    if (severity === "warning") return 2;
    return 1;
};

/*
 * Returns a list of insights with unique types, keeping only the insight with the highest severity for each type.
 */
export const getUniqueInsightsWithMaxSeverity = (insights: CustomerInsightDetail[]): CustomerInsightDetail[] => {
    const grouped = Map.groupBy(insights, (insight) => insight.type);
    return grouped
        .values()
        .map((typeInsights) =>
            typeInsights.reduce((max, current) =>
                getSeverityPriority(current.severity) > getSeverityPriority(max.severity) ? current : max,
            ),
        )
        .toArray();
};

/*
 * Calculates a score based on the number of unique insight types and their severities.
 */
const getUniqueInsightsAndSeverityScore = (insights: CustomerInsightDetail[]): number => {
    const uniqueMaxSeverityInsights = getUniqueInsightsWithMaxSeverity(insights);
    // Sum of severity of each unique insight type's priority,
    // e.g. 2 different insight types with respective severities "critical" (3) and "warning" (2) = 5
    const severitySum = uniqueMaxSeverityInsights.reduce(
        (sum, insight) => sum + getSeverityPriority(insight.severity),
        0,
    );
    return severitySum;
};

/*
 * Calculates a weight for a list of insights based on the number of unique types, their severities, and total number of insights.
 */
export const calculateInsightsWeight = (insights: CustomerInsightDetail[]): number => {
    // First, sort by number of unique insights, weighted by their severities
    const uniqueInsightsScore = getUniqueInsightsAndSeverityScore(insights);
    // Multiplier for higher sorting priority
    const uniqueInsightsScoreMultiplied = uniqueInsightsScore * 1000;

    // Then by total number of insights
    const insightsCount = insights.length;
    return uniqueInsightsScoreMultiplied + insightsCount;
};
